// CSG palette — the single token source for the whole dashboard.
//
// Mechanism: spec docs/superpowers/specs/2026-09-24-dashboard-claude-theme-design.md
// (§3, §4). Values: the CSG Brand Guidelines 2025 as carried by the csg-branding
// skill (skill/references/brand-tokens.md), swapped in on 2026-09-25 for the
// Chat-UI terracotta set. The brand names five colours; a UI needs a surface
// ladder, hover stops and readable secondary text as well, so every derived
// value here is a mix of two brand colours (the same way the brand's own
// `#C9D6E4` hairline is Alice Blue pulled towards Lapis Lazuli). The mixes and
// their WCAG ratios were checked at swap time: every text stop clears 4.5:1 on
// the surfaces it is used on (tertiary text never sits on the rail).
//
// Light is the brand on white; dark is neutral grey/near-black with the brand in
// the accents. Two accents, on purpose. `accent` is the FILL for primary actions and
// selected states: Oxford Blue with white text in light (17.6:1), Celestial Blue with
// Oxford text in dark (5.7:1) — white on Celestial is only 3.1:1, which is why
// Celestial cannot fill a button. `highlight` is Celestial in BOTH modes for the
// thin accents the brand uses it for: tab ink bar, progress, focus ring, the
// active-nav glyph. Text in the accent hue (links, kickers) is `--odin-accent-text`
// = status.active.text: Lapis Lazuli in light, a lifted Celestial in dark.
//
// Because the accent differs per mode, NOTHING in VARS is literal hex any more:
// call sites that used to append a two-digit alpha suffix use color-mix().
// Both palettes have IDENTICAL key sets — __tests__/claudeTokens.test.js
// enforces it — because ClaudeVars loops them to emit one `--odin-*` per leaf.
//
// Rule 12 (ODIN1-Dashboard/CLAUDE.md): every colour in the app comes from
// here, via the `--odin-*` variables (VARS) or `theme.colors`. Hard-coded
// hex outside this file is forbidden (tested by paletteLiteralsGuard).

export const BRAND = Object.freeze({
  oxfordBlue: '#001641', // primary: titles, headings, dark bands, primary fills (light)
  celestialBlue: '#009CDC', // primary: controlled accents; the "[it]" of the wordmark
  lapisLazuli: '#005A96', // secondary: links, labels, secondary emphasis
  aliceBlue: '#E1EBF4', // secondary: panels, callouts, table alternation
  white: '#FFFFFF',
  black: '#000000',
  hairline: '#C9D6E4', // derived by the brand: Alice Blue towards Lapis, gridlines only
});

const status = (completed, failed, active, awaiting) => ({
  completed: { bg: completed[0], text: completed[1], border: completed[2] },
  failed: { bg: failed[0], text: failed[1], border: failed[2] },
  active: { bg: active[0], text: active[1], border: active[2] },
  awaiting: { bg: awaiting[0], text: awaiting[1], border: awaiting[2] },
});

export const LIGHT = {
  bg: '#F3F7FB', // page canvas: Alice Blue 40% on white, so white cards lift off it
  surface: '#E1EBF4', // sidebar rail, default tag, code panel: Alice Blue (brand panel)
  surfaceHover: '#D5E0EC', // Alice Blue halfway to the hairline
  surfaceElevated: '#FFFFFF', // cards: Pristine White
  chrome: '#F9FBFD', // header, pills, light controls: Alice Blue 20% on white
  ink: '#001641', // display ink: Oxford Blue headings, stat values, active nav
  textPrimary: '#000D27', // body: black pulled 40% towards Oxford Blue (19.3:1 white)
  textSecondary: '#52617E', // Oxford Blue 68% on white: 6.1 white · 5.7 page · 5.0 rail
  textTertiary: '#5E6C87', // Oxford Blue 63% on white: 5.0 white · 4.7 page — never on the rail
  border: '#C9D6E4', // hairline: cards, dividers, table rows (the brand gridline)
  borderStrong: '#9EABBE', // control edge: Oxford Blue 30% on Alice Blue
  codeBg: '#E1EBF4', // command block = Alice Blue panel
  accent: '#001641', // primary FILL: Oxford Blue (white text, 17.6:1)
  accentHover: '#002A5A', // Oxford Blue 30% towards Lapis
  danger: '#C0352B',
  onAccent: '#FFFFFF', // text painted on an accent-filled control
  onHighlight: '#001641', // text on a highlight (Celestial) fill: Oxford Blue in both modes (5.7:1); white is only 3.1:1
  highlight: '#009CDC', // Celestial Blue: ink bar, progress, focus, thin glyphs (never body text)
  // HSL triplet of Celestial Blue, used as `hsl(var(--odin-accent-focus) / 0.4)`
  accentFocus: '197 100% 43%',
  // Info is the brand callout: Alice Blue panel, Lapis text (6.0:1), hairline edge
  info: { bg: '#E1EBF4', text: '#005A96', border: '#C9D6E4' },
  // The Management icons' glass fill (features/management/icons): a white-to-Alice-Blue
  // gradient at rest, a Celestial tint when active (the status.active pair).
  icon: { glassTop: '#FFFFFF', glassBottom: '#D5E0EC', glassOnTop: '#E0F3FB', glassOnBottom: '#A6DCF3' },
  // The Dashboard hero band (features/management/hero): an Oxford Blue band with white text and a
  // Celestial-tint kicker; the art's light files fade into this exact band colour.
  hero: { band: '#001641', ink: '#FFFFFF', kicker: '#A6DCF3' },
  // The Management calendar's people palette (features/management/calendar): seven categorical hues,
  // validated for colour-blind separation on the white card (dataviz palette check); `other` is for
  // past seven people and released owners. Functional like `status`, so outside the brand ramp.
  people: { p1: '#2A78D6', p2: '#EB6834', p3: '#1BAF7A', p4: '#EDA100', p5: '#E87BA4', p6: '#008300', p7: '#4A3AA7', other: '#8B97AB' },
  // Cool, two-layer shadows tinted with Oxford Blue carry the depth between the planes
  shadow: {
    card: '0 1px 2px rgba(0, 22, 65, 0.05), 0 10px 28px -14px rgba(0, 22, 65, 0.16)',
    cardHover: '0 1px 2px rgba(0, 22, 65, 0.06), 0 16px 36px -14px rgba(0, 22, 65, 0.22)',
    chrome: '0 1px 0 rgba(0, 22, 65, 0.05), 0 8px 20px -16px rgba(0, 22, 65, 0.2)',
    pill: '0 1px 2px rgba(0, 22, 65, 0.06)',
    icon: '0 1px 1.2px rgba(0, 22, 65, 0.22)', // drop-shadow() under an icon's glass fill
    float: '0 2px 4px rgba(0, 22, 65, 0.06), 0 18px 40px -16px rgba(0, 22, 65, 0.3)', // a card lifted over the hero band
    primary: '0 1px 2px rgba(0, 22, 65, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.14)',
  },
  // Functional status colours (the brand has none): text clears 4.5:1 on its own
  // tint at 12px — 5.1 / 5.1 / 6.3 / 4.6. Active/processing is the brand blue pair.
  status: status(
    ['#E4F3EA', '#2A7147', '#BFE0CB'],
    ['#FBEAEA', '#B3392C', '#F0C6C1'],
    ['#E0F3FB', '#005A96', '#A6DCF3'],
    ['#FDF3DC', '#95660A', '#F0DFAE'],
  ),
};

export const DARK = {
  // Neutral greys and near-black (James, 2026-09-25: preferred over an Oxford Blue
  // ramp). The brand shows in the accents, chips and links, not the surfaces.
  bg: '#161718', // page canvas
  surface: '#0F1012', // rail: near-black
  surfaceHover: '#2A2D31',
  surfaceElevated: '#222529', // cards
  chrome: '#1A1B1D', // header, code panel
  ink: '#FFFFFF',
  textPrimary: '#F2F4F7', // 14.3 card · 16.4 page
  textSecondary: '#A6ACB5', // 6.9 card · 7.9 page
  textTertiary: '#8B919A', // 5.0 card · 5.7 page
  border: '#2E3136',
  borderStrong: '#474B52',
  codeBg: '#1A1B1D',
  accent: '#009CDC', // primary FILL: Celestial Blue (Oxford text, 5.7:1; 5.1:1 against a card)
  accentHover: '#26ABE1', // Celestial 15% towards white
  danger: '#C0352B',
  onAccent: '#001641', // Oxford Blue text on the Celestial fill
  onHighlight: '#001641', // text on a highlight (Celestial) fill: Oxford Blue in both modes (5.7:1); white is only 3.1:1
  highlight: '#009CDC',
  accentFocus: '197 100% 43%',
  info: { bg: '#132238', text: '#4DBAE6', border: '#1F3A5C' }, // blue-tinted callout on grey, 7.2:1
  // Neutral grey glass at rest, the deep-blue status.active pair when active.
  icon: { glassTop: '#3A3E44', glassBottom: '#1E2227', glassOnTop: '#17436A', glassOnBottom: '#0F2A44' },
  // The band is the page colour, so it blends in; the art's dark files fade into it.
  hero: { band: '#161718', ink: '#FFFFFF', kicker: '#4DBAE6' },
  // The same seven hues stepped for the dark card (all at least 3:1 on #222529).
  people: { p1: '#3987E5', p2: '#D95926', p3: '#199E70', p4: '#C98500', p5: '#D55181', p6: '#008300', p7: '#9085E9', other: '#6E7683' },
  shadow: {
    card: '0 1px 2px rgba(0, 0, 0, 0.35), 0 12px 28px -14px rgba(0, 0, 0, 0.45)',
    cardHover: '0 1px 2px rgba(0, 0, 0, 0.4), 0 18px 36px -14px rgba(0, 0, 0, 0.55)',
    chrome: '0 1px 0 rgba(0, 0, 0, 0.4), 0 8px 20px -16px rgba(0, 0, 0, 0.6)',
    pill: '0 1px 2px rgba(0, 0, 0, 0.3)',
    icon: '0 1px 1.2px rgba(0, 0, 0, 0.6)',
    float: '0 2px 4px rgba(0, 0, 0, 0.4), 0 20px 40px -16px rgba(0, 0, 0, 0.7)',
    primary: '0 1px 2px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.14)',
  },
  // 7.0 / 6.8 / 6.6 / 7.2 on their own tints; active/processing is Celestial on deep blue
  status: status(
    ['#10382F', '#7FCF9E', '#1F5A45'],
    ['#3B1F2E', '#F09A8E', '#5E3140'],
    ['#0F2A44', '#4DBAE6', '#17436A'],
    ['#3B3218', '#E3BE6A', '#5C4D26'],
  ),
};

// Mode-independent scale. Gabarito is the brand face (Google Fonts, loaded in
// index.html; Avenir is the guidelines' named substitute). The brand has no
// serif, so display type is Gabarito Bold. Cascadia Mono is the brand's one
// permitted non-Gabarito face, for command text and code only, self-hosted from
// public/fonts (GlobalStyles declares the @font-face).
const GABARITO = "'Gabarito', 'Avenir Next', Avenir, system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
export const SCALE = {
  radiusSm: '6px',
  radiusMd: '12px',
  radiusLg: '18px',
  radiusXl: '24px',
  fontSans: GABARITO,
  fontDisplay: GABARITO,
  fontMono: "'Cascadia Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
};

const kebab = (s) => s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

// `--odin-bg`, `--odin-status-completed-text`, … — one variable per leaf.
export const varName = (path) => `--odin-${path.map(kebab).join('-')}`;

// Depth-first walk of a palette, yielding [path, value] for every leaf.
const leaves = (obj, path = []) =>
  Object.entries(obj).flatMap(([key, value]) =>
    value && typeof value === 'object' ? leaves(value, [...path, key]) : [[[...path, key], value]],
  );

// Same shape as LIGHT, every value a `var(--odin-…)` string. Alpha tints are
// `color-mix(in srgb, var(--odin-accent) 13%, transparent)` at the call site.
const buildVars = (obj, path = []) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      value && typeof value === 'object'
        ? buildVars(value, [...path, key])
        : `var(${varName([...path, key])})`,
    ]),
  );

export const VARS = buildVars(LIGHT);

// The declarations (no selector) for one palette, plus the semantic aliases.
export const toCssBlock = (palette) => {
  const lines = leaves(palette).map(([path, value]) => `  ${varName(path)}: ${value};`);
  lines.push(
    `  --odin-success: ${palette.status.completed.text};`,
    `  --odin-warning: ${palette.status.awaiting.text};`,
    `  --odin-error: ${palette.status.failed.text};`,
    `  --odin-info: ${palette.info.text};`,
    // The accent hue read as TEXT or a thin glyph (links, link buttons, the checked
    // radio, kickers) uses this AA stop — Lapis Lazuli in light — not the fill.
    `  --odin-accent-text: ${palette.status.active.text};`,
  );
  return lines.join('\n');
};
